import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { usePortal } from "../context/PortalContext";
import { useUserAuth } from "../context/AuthContext";
import { buildDraftKey, getDraftUserId, mergeSessionDraft, readSessionDraft } from "../helpers/SessionStorageHelpers";
import { usePageBackgroundColor } from "../hooks/usePageBackgroundColor";
import { IClientData } from "../models/ClientData";
import { IQuote } from "../models/QuoteData";
import { getFlattenedData } from "../helpers/SurveyJSHelpers";
import { setLeadTrackingData } from "../helpers/setLeadTrackingData";
import { endBehaviourJourney, startBehaviourJourney, trackBehaviourEvent } from "../helpers/userBehaviourTrackingHelpers";
import { SurveyJSForm } from "../modules/surveyjs_forms/SurveyJSForm";
import { lv2Theme } from "../modules/surveyjs_forms/surveyTheme";
import { LanderV2FunnelNav } from "../modules/common/menus/LanderV2FunnelNav";
import { getLanderV2Campaign } from "./Campaigns/Schemas/LanderV2/registry";
import { LanderV2CampaignConfig, LanderV2StepConfig } from "./Campaigns/Schemas/LanderV2/types";
import { resolveLanderV2Headline } from "./Utils/resolveLanderV2Headline";
import { buildLeadPayload, draftAnswers } from "./Utils/buildLanderLeadPayload";
import { useStepTransition } from "./journey/useStepTransition";
import { useStickyBottomBar } from "./journey/useStickyBottomBar";
import { pushLanderFormSubmitEvent } from "./Utils/pushLanderFormSubmitEvent";
import "./Utils/styles/LanderV2.css";

const sourceSegments = ["google", "bing", "meta"];

type LeadStage = "none" | "partial" | "lead";

/** This lander's progress, kept in the visitor's session draft under "lander:<slug>". */
interface LanderState {
  source: string;
  landingUrl: string;
  completedSteps: string[];
  leadStage: LeadStage;
  quotes: IQuote[];
  selectedQuote: IQuote | null;
  /** Lander-specific progress (LanderV2StepProps.journeyData). */
  data?: Record<string, any>;
}

const leadStageOrder: LeadStage[] = ["none", "partial", "lead"];

/** The step form's own answers; an empty answer removes that field from the draft. */
function stepAnswers(step: LanderV2StepConfig, data: Record<string, any>) {
  const flat = (getFlattenedData(data) ?? {}) as Record<string, unknown>;
  return Object.fromEntries(
    step.schemaPage.elements.map((element: { name: string }) => [element.name, flat[element.name]])
  );
}

function wait(ms: number) {
  return new Promise<boolean>((resolve) => setTimeout(() => resolve(true), ms));
}

export default function LanderV2() {
  const { campaignSlug } = useParams<{ campaignSlug: string }>();
  const campaign = getLanderV2Campaign(campaignSlug);

  if (!campaign) {
    return (
      <div>
        Page not found... <Link to="/">Go back to home</Link>
      </div>
    );
  }

  return <LanderV2Journey key={campaign.slug} campaign={campaign} />;
}

function LanderV2Journey({ campaign }: { campaign: LanderV2CampaignConfig }) {
  const params = useParams<{ "*": string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const portal: any = usePortal();
  const { user } = useUserAuth();
  const { isLoading, transition } = useStepTransition();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [, setDraftRevision] = useState(0);

  // The session draft is the one record of the visitor's answers; its key never changes mid-journey.
  const draftKey = buildDraftKey(getDraftUserId(user));
  const stateKey = `lander:${campaign.slug}`;
  const readDraft = () => (readSessionDraft(draftKey)?.data ?? {}) as Record<string, any>;
  const draft = readDraft();
  const journey = draft[stateKey] as LanderState | undefined;

  const campaignRoute = `/landers/v2/${campaign.slug}`;
  const segment = (params["*"] ?? "").split("/").filter(Boolean)[0] ?? "";
  const source = sourceSegments.includes(segment) ? segment : "";
  const isLanding = !segment || Boolean(source);
  const entryStep = campaign.steps.find((candidate) => candidate.isEntry)!;
  const routedSteps = campaign.steps.filter((candidate) => !candidate.isEntry);
  const funnelSteps = routedSteps.filter((candidate) => !candidate.offFunnel);
  const step = isLanding ? undefined : routedSteps.find((candidate) => candidate.slug === segment);

  const headline = useMemo(() => resolveLanderV2Headline(campaign, location.search), [campaign, location.search]);
  const stickyFormRef = useRef<HTMLDivElement>(null);
  useStickyBottomBar(stickyFormRef, {
    enabled: Boolean(step?.stickyCompleteButton && !step.Component),
    barSelector: ".sd-body__navigation",
    widthSelector: ".sd-page",
    resetKey: step?.slug,
  });
  const lead = draftAnswers(draft) as IClientData;

  usePageBackgroundColor(isLanding ? "#ffffff" : undefined);

  useEffect(() => {
    if (!source) return;
    localStorage.setItem("lander_ad_source", source);
  }, [source]);

  useEffect(() => {
    if (!campaign.funnelTracking) {
      endBehaviourJourney();
      return;
    }
    startBehaviourJourney({ lander: campaign.slug, source: source || journey?.source });
  }, [campaign, journey?.source, source]);

  // Once each time the landing page is shown; draft writes on the landing page must not re-send it.
  useEffect(() => {
    if (campaign.funnelTracking && isLanding) trackBehaviourEvent("Lander Page View");
  }, [campaign.funnelTracking, isLanding]);

  // Step view events: sent on each in-app move between steps (buttons, Back or the browser's back/forward), never for
  // the page load's first step, so a pasted or refreshed URL sends nothing. The step and journey are read from a ref
  // because the journey is re-read from the draft on every render.
  const currentStepSlug = isLanding ? "landing" : segment;
  const previousStepRef = useRef<string | null>(null);
  const latestRef = useRef({ step, journey });
  latestRef.current = { step, journey };
  useEffect(() => {
    const from = previousStepRef.current;
    previousStepRef.current = currentStepSlug;
    const { step: viewed, journey: state } = latestRef.current;
    const view = viewed?.funnel?.view;
    if (from === null || from === currentStepSlug || !view || !state) return;
    const extra = viewed.funnel?.viewProperties?.({ quotes: state.quotes, selectedQuote: state.selectedQuote, from }) ?? {};
    trackBehaviourEvent(view, { from, ...extra });
  }, [currentStepSlug]);

  // Stable handlers: SurveyJSForm re-arms its first-interaction listener whenever these change.
  const handleEntryFirstInteraction = useCallback(() => {
    if (entryStep.funnel?.start) trackBehaviourEvent(entryStep.funnel.start);
  }, [entryStep]);
  const handleStepFirstInteraction = useCallback(() => {
    if (step?.funnel?.start) trackBehaviourEvent(step.funnel.start);
  }, [step]);

  const stepPath = useCallback(
    (slug: string) => `${campaignRoute}/${slug}${location.search}`,
    [campaignRoute, location.search]
  );
  const landingPath = `${campaignRoute}${journey?.source ? `/${journey.source}` : ""}${location.search}`;
  const goToStep = useCallback((slug: string) => navigate(stepPath(slug)), [navigate, stepPath]);
  const goHome = useCallback(() => navigate(landingPath), [navigate, landingPath]);

  const writeDraft = (patch: Record<string, unknown>) => {
    mergeSessionDraft(draftKey, patch);
    setDraftRevision((revision) => revision + 1);
  };

  const updateJourney = (change: (current: LanderState) => LanderState) => {
    const current = readDraft()[stateKey] as LanderState | undefined;
    if (current) writeDraft({ [stateKey]: change(current) });
  };

  const completeStep = (slug: string) =>
    updateJourney((current) =>
      current.completedSteps.includes(slug) ? current : { ...current, completedSteps: [...current.completedSteps, slug] }
    );

  // Starts this lander's journey once; the session id is set here and never regenerated.
  const ensureJourney = () => {
    const current = readDraft();
    if (current[stateKey]) return;
    const newJourney: LanderState = {
      source,
      landingUrl: `${window.location.origin}${location.pathname}${location.search}`,
      completedSteps: [],
      leadStage: "none",
      quotes: [],
      selectedQuote: null,
    };
    writeDraft({
      sessionId: new URLSearchParams(location.search).get("sid") || current.sessionId || uuidv4(),
      [stateKey]: newJourney,
    });
  };

  const buildPayload = () => {
    const current = readDraft();
    const landingUrl = new URL((current[stateKey] as LanderState).landingUrl);
    Object.entries(campaign.forcedUrlParams ?? {}).forEach(([name, value]) => landingUrl.searchParams.set(name, value));
    const payload = buildLeadPayload(current, landingUrl.toString(), campaign);
    return campaign.transformLeadPayload ? campaign.transformLeadPayload(payload) : payload;
  };

  const buildTrackedPayload = (): IClientData => {
    const built = buildPayload();
    return { ...built, ...setLeadTrackingData(built) };
  };

  // Every save sends the whole draft, built at the moment it is sent. lead/update doesn't add the landing link's
  // tracking (UTMs, click id) the way the partial insert does, and creates the lead when an ad-hoc booking is the first save.
  const saveLead = async (stage: Exclude<LeadStage, "none">, oneOff: Partial<IClientData> = {}) => {
    if (!readDraft()[stateKey]) return false;
    const payload = { ...buildTrackedPayload(), ...oneOff };
    const code =
      stage === "partial" ? await portal.insertPartialLeadData(payload) : await portal.apiUpdateLeadData(payload);
    if (code !== 200) {
      trackBehaviourEvent("Lander Error", { stage });
      return false;
    }
    updateJourney((current) =>
      leadStageOrder.indexOf(stage) > leadStageOrder.indexOf(current.leadStage) ? { ...current, leadStage: stage } : current
    );
    return true;
  };

  const fetchQuotes = async () => {
    if (!readDraft()[stateKey]) return false;
    const payload = buildPayload();
    const result = await portal.getNewAnonymousQuickQuotes(payload);
    if (typeof result === "number" || result?.statusCode !== 200) {
      trackBehaviourEvent("Lander Error", { stage: "quotes" });
      return false;
    }
    const quotes: IQuote[] = result.data ?? [];
    updateJourney((latest) => ({
      ...latest,
      quotes,
      selectedQuote: quotes[0] ?? null,
    }));
    portal.setAnonymousClientData?.((existing: any) => ({
      ...(existing ?? {}),
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      primaryPhone: payload.primaryPhone,
      dateOfBirth: payload.dateOfBirth,
      postcode: payload.postcode,
      quotes,
      annuity: { ...(existing?.annuity ?? {}), ...(payload.annuity ?? {}), quotes },
      isAnonymous: true,
      canContinueToPortal: true,
    }));
    return true;
  };

  const trackStepComplete = (completed: LanderV2StepConfig) =>
    completed.funnel?.complete?.forEach((name) => trackBehaviourEvent(name));

  const handleEntrySubmit = (data: Record<string, any>) => {
    if (isLoading) return;
    setSubmitError(null);
    ensureJourney();
    writeDraft(stepAnswers(entryStep, data));
    completeStep(entryStep.slug);
    void saveLead("partial").then((saved) => saved && trackStepComplete(entryStep));

    const next = stepPath(funnelSteps[0].slug);
    if (entryStep.LoadingComponent && entryStep.fixedLoadingMs) {
      void transition(() => wait(entryStep.fixedLoadingMs!), next);
    } else {
      navigate(next);
    }
  };

  const handleStepComplete = (completed: LanderV2StepConfig, data: Record<string, any>) => {
    if (isLoading || !completed.next) return;
    setSubmitError(null);
    writeDraft(stepAnswers(completed, data));

    if (!completed.fetchesQuotes) {
      completeStep(completed.slug);
      void saveLead("partial").then((saved) => saved && trackStepComplete(completed));
      navigate(stepPath(completed.next));
      return;
    }

    void transition(async () => {
      const isFirstLead = readDraft()[stateKey]?.leadStage !== "lead";
      if (!(await saveLead("lead"))) {
        setSubmitError("We could not save your details. Please try again.");
        return false;
      }
      if (isFirstLead) pushLanderFormSubmitEvent(buildPayload());
      trackStepComplete(completed);
      if (!(await fetchQuotes())) {
        setSubmitError("We could not fetch your quotes. Please try again.");
        return false;
      }
      completeStep(completed.slug);
      // Sent before the move to the quotes step, so it precedes that step's "Quotes Viewed".
      const quoteCount = (readDraft()[stateKey] as LanderState).quotes.length;
      trackBehaviourEvent("Quotes Received", { quote_count: String(quoteCount) });
      return true;
    }, stepPath(completed.next));
  };

  const saveLeadFields = async (fields: Partial<IClientData>, oneOff?: Partial<IClientData>) => {
    writeDraft(fields);
    return saveLead("lead", oneOff);
  };

  const handleBookAppointment = () => {
    ensureJourney();
    goToStep("book-appointment");
  };

  // Old single-URL links (e.g. /google#quotes) go to that link's landing page, keeping its ad source.
  if (location.hash) {
    return <Navigate to={`${campaignRoute}${source ? `/${source}` : ""}${location.search}`} replace />;
  }

  if (isLanding) {
    const LoadingComponent = entryStep.LoadingComponent;
    return (
      <main className={`lv2-page lv2-page-${campaign.slug} lv2-home`}>
        {LoadingComponent && isLoading && <LoadingComponent />}
        <campaign.MarketingComponent
          entrySchemaPage={entryStep.schemaPage}
          initialData={entryStep.initialData}
          surveyProps={{ ...campaign.surveyProps, completeText: entryStep.completeText }}
          resolvedHeading={headline.heading}
          resolvedSubheading={headline.subheading}
          landingPageTheme={campaign.landingPageTheme}
          landingPageVariation={campaign.landingPageVariation}
          landingPageVersion={campaign.landingPageVersion}
          submitError={submitError}
          onQuoteSubmit={handleEntrySubmit}
          onFirstInteraction={handleEntryFirstInteraction}
          onBookAppointment={
            routedSteps.some((candidate) => candidate.slug === "book-appointment") ? handleBookAppointment : undefined
          }
        />
      </main>
    );
  }

  if (!step || !journey) {
    return <Navigate to={`${campaignRoute}${location.search}`} replace />;
  }

  // A step is reachable only once every form step before it has been completed.
  const missingStep = step.offFunnel
    ? undefined
    : campaign.steps
        .slice(0, campaign.steps.indexOf(step))
        .find((candidate) => !candidate.Component && !journey.completedSteps.includes(candidate.slug));
  if (missingStep) {
    return <Navigate to={missingStep.isEntry ? landingPath : stepPath(missingStep.slug)} replace />;
  }

  const stepIndex = funnelSteps.indexOf(step);
  const LoadingComponent = step.LoadingComponent;
  const showVlm3PersonalDetailsRating = campaign.slug === "annuitycalculator-ux2-vlm3" && step.slug === "personal-details";

  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else if (step.previous) {
      goToStep(step.previous);
    } else {
      goHome();
    }
  };

  return (
    <main className={`lv2-page lv2-page-${campaign.slug} lv2-step-page${step.Component ? " lv2-component-step-page" : ""}`}>
      <LanderV2FunnelNav
        startOver={step.startOverOnNav ?? Boolean(step.Component)}
        onBack={handleBack}
        onStartOver={goHome}
      />
      {LoadingComponent && isLoading && <LoadingComponent />}

      <section className="lv2-step-shell">
        {step.illustrationSrc && (
          <img
            className="lv2-step-illustration"
            src={step.illustrationSrc}
            alt={step.illustrationAlt ?? ""}
            aria-hidden={step.illustrationAlt ? undefined : true}
          />
        )}
        {!step.Component && (
          <div className="lv2-progress" aria-hidden="true">
            <span style={{ width: `${((stepIndex + 1) / funnelSteps.length) * 100}%` }} />
          </div>
        )}

        {!step.Component && step.heading && <h1 className="lv2-step-heading">{step.heading}</h1>}

        {step.Component ? (
          <step.Component
            lead={lead}
            quotes={journey.quotes}
            selectedQuote={journey.selectedQuote}
            onSelectQuote={(quote) => updateJourney((current) => ({ ...current, selectedQuote: quote }))}
            goToStep={goToStep}
            goHome={goHome}
            saveLead={saveLeadFields}
            writeAnswers={writeDraft}
            buildLead={buildTrackedPayload}
            journeyData={journey.data ?? {}}
            updateJourneyData={(patch) =>
              updateJourney((current) => ({ ...current, data: { ...(current.data ?? {}), ...patch } }))
            }
          />
        ) : (
          <>
            <div ref={stickyFormRef} className={step.stickyCompleteButton ? "lv2-sticky" : undefined}>
              <SurveyJSForm
                key={step.slug}
                surveyFormId={`${campaign.surveyFormId}-${step.slug}`}
                pageSchemas={[step.schemaPage]}
                initialData={step.initialData}
                clearDraftOnComplete={false}
                surveyProps={{ ...campaign.surveyProps, completeText: step.completeText }}
                surveyTheme={lv2Theme}
                wrapperFormCssClass={`lv2-survey ${step.layoutClassName ?? ""}`.trim()}
                suppressLoader
                keepFormVisibleOnComplete
                disableCompleteUntilCurrentPageComplete={step.disableCompleteUntilCurrentPageComplete}
                disableTokenPrefill={step.disableTokenPrefill}
                onFirstInteraction={handleStepFirstInteraction}
                onComplete={(data) => handleStepComplete(step, data ?? {})}
              />
            </div>
            {submitError && <div className="lv2-status lv2-status-error">{submitError}</div>}
          </>
        )}
        {showVlm3PersonalDetailsRating && (
          <div className="pa-v1-rating pa-v1-personal-rating">
            <span>Rated <strong>Excellent</strong></span>
            <span className="pa-v1-rating-stars" aria-label="5 star rating">
              <i>★</i>
              <i>★</i>
              <i>★</i>
              <i>★</i>
              <i>★</i>
            </span>
          </div>
        )}
      </section>
    </main>
  );
}
